import React from "react";
import { Box, CircularProgress } from "@mui/material";
import { AuthGuard, NoAppAccess, platformApps } from "@scoutingops/ui";
import type { AppAccessDescriptor } from "@scoutingops/security";

import { appConfig } from "@/config/app";
import { useAppAccess } from "@/hooks/use-app-access";

/**
 * The exact descriptor the app switcher gates the Site tile on, taken from the
 * shared registry rather than restated here. Restating it is how a tile that is
 * visible ends up opening an app that refuses entry (or the reverse) — deriving
 * it means the switcher and this guard cannot disagree. It also spares this
 * file the two names that don't match: the switcher key is "site-builder", the
 * catalog app is "sitebuilder", and the entitlement is "site.core".
 *
 * Split into its two halves so the refusal page can say which gate refused: a
 * missing entitlement is something the unit can buy, and pointing at billing
 * helps; a missing grant is something only a unit admin can give, and pointing
 * at billing would be a dead end.
 */
const siteApp = platformApps.find((app) => app.key === "site-builder");
const SITE_ENTITLEMENT_ONLY: AppAccessDescriptor = { entitlementKey: siteApp?.entitlementKey };
const SITE_PERMISSION_ONLY: AppAccessDescriptor = {
  permissionApp: siteApp?.permissionApp,
  permissionFeature: siteApp?.permissionFeature,
};

export interface AppAccessGuardProps {
  children: React.ReactNode;
}

/**
 * Authorization to the whole app, one level below @scoutingops/ui's AuthGuard
 * (which only establishes that somebody is signed in). Without this, every
 * signed-in member of any org reached the Studio regardless of the unit's
 * grants or the org's plan — the app switcher hid the tile, but a hidden tile
 * is not a gate.
 *
 * Waits on `isLoading` rather than calling hasAppAccess() meanwhile: the gate
 * fails open while its inputs are unknown (see resolveAppAccess), which is the
 * right default for a denial but would flash the whole app to someone about to
 * be refused.
 *
 * Entitlement first, matching resolveAppAccess's own order: an admin of an org
 * whose plan excludes Site still has nothing to do here, so that reason
 * outranks any grant they hold.
 *
 * The unit's published public website is served outside this app's
 * authenticated area and is deliberately untouched by any of this.
 *
 * UI noise reduction, not enforcement — RLS via app.has_unit_permission() is
 * the boundary, and every table this app reads is covered there.
 */
export function AppAccessGuard({ children }: AppAccessGuardProps): React.JSX.Element {
  const { hasAppAccess, isLoading } = useAppAccess();

  if (isLoading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", minHeight: "60dvh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasAppAccess(SITE_ENTITLEMENT_ONLY)) return <NoAppAccess appName={appConfig.name} reason="entitlement" />;
  if (!hasAppAccess(SITE_PERMISSION_ONLY)) return <NoAppAccess appName={appConfig.name} reason="permission" />;

  return <>{children}</>;
}

/**
 * Authentication then authorization, in that order, as one component — every
 * authenticated surface in this app should mount behind exactly this.
 *
 * The order is load-bearing: AppAccessGuard's underlying queries only run for
 * a signed-in user with an active organization, so putting it outside
 * AuthGuard would have it decide before there is anything to decide on.
 */
export function SiteGuard({ children }: AppAccessGuardProps): React.JSX.Element {
  return (
    <AuthGuard>
      <AppAccessGuard>{children}</AppAccessGuard>
    </AuthGuard>
  );
}
